import styled from "@emotion/styled";
import {
  GelBoxLayout,
  GelContainerLite,
  GelHeading6,
  GelIcon,
  GelLink,
  GelParagraph,
} from "@tal-gel/components";
import { getGelTokens } from "@tal-gel/theming";
import HealthTShirt from "@tal-gel/theming-plugin-tal/dist/lite/brand-icons/health-t-shirt";
import HealthShade from "@tal-gel/theming-plugin-tal/dist/lite/brand-icons/health-shade";
import HealthSunglasses from "@tal-gel/theming-plugin-tal/dist/lite/brand-icons/health-sunglasses";
import HealthSunscreen from "@tal-gel/theming-plugin-tal/dist/lite/brand-icons/health-sunscreen";
import HealthHat from "@tal-gel/theming-plugin-tal/dist/lite/brand-icons/health-hat";

const prevention = [
  {
    title: "Slip",
    iconName: "HealthTShirt",
    content:
      "Wear clothing that covers as much skin as possible. The best protection comes from closely woven fabrics. For clothes designed for sun protection, the higher the UPF (ultraviolet protection factor), the greater the protection.",
  },
  {
    title: "Slop",
    iconName: "HealthSunscreen",
    content:
      "Apply a water-resistant sunscreen with SPF 30+ or higher at least 20 minutes before going outside, as it takes this long to sink into the skin. Reapply every two hours, after swimming and after any activity that causes you to sweat or rub the sunscreen off.",
  },
  {
    title: "Slap",
    iconName: "HealthHat",
    content: "Wear a broad-brimmed hat that protects your face, head, neck and ears.",
  },
  {
    title: "Seek",
    iconName: "HealthShade",
    content:
      "Use shade from trees, umbrellas, buildings or any type of canopy. Be aware that UV radiation is reflective and bounces off surfaces such as concrete, snow, water, soil and sand, causing sun damage even when you think you are shaded.",
  },
  {
    title: "Slide",
    iconName: "HealthSunglasses",
    content:
      "Wear sunglasses that meet the Australian and New Zealand standard AS/NZS 1067:2003 and have an EPF (eye protection factor) of 10. Close-fitting, wrap-around styles are best.",
  },
];

const renderBrandIcon = (icon) => {
  switch (icon) {
    case "HealthTShirt":
      return HealthTShirt;
    case "HealthSunscreen":
      return HealthSunscreen;
    case "HealthHat":
      return HealthHat;
    case "HealthShade":
      return HealthShade;
    case "HealthSunglasses":
      return HealthSunglasses;
    default:
      return null;
  }
};

const PreventSkinCancer = () => {
  return (
    <>
      {/* <CardHeader
        title="Skin cancer prevention"
        description="Most skin cancers can be prevented by protecting your skin and minimising your sun exposure."
        backgroundUrl="/images/skin-cancer-prevention.jpg"
      ></CardHeader> */}

      <GelParagraph
        marginTop={getGelTokens().global.sizeBaseX6}
        marginBottom={getGelTokens().global.sizeBaseX4}
      >
        Australia has one of the highest rates of skin cancer in the world.<sup>2</sup>
      </GelParagraph>
      <GelParagraph marginBottom={getGelTokens().global.sizeBaseX4}>
              The good news is that skin cancer can be prevented. Over 95% of skin cancers are caused by overexposure to ultraviolet (UV) radiation from the sun. You can put steps in place to protect yourself and your family from harmful UV radiation and reduce your risk.<sup>2</sup>
      </GelParagraph>
      <GelParagraph marginBottom={getGelTokens().global.sizeBaseX4}>
              UV radiation can be high even when it’s not hot and sunny, however, research indicates that nearly 60% of Australians are likely to be more relaxed about sun protection on days that are overcast and less hot.<sup>5</sup>
      </GelParagraph>
      <GelParagraph>
              For the best protection, minimise your sun exposure and use a combination of sun protection measures (such as wearing sunscreen daily) to protect your skin whenever the UV level is 3 or above.<sup>2</sup>  To stay on top of UV levels throughout the day in over 600 locations, you can download the free {" "}
        <GelLink href="https://www.sunsmart.com.au/resources/sunsmart-app" target="_blank">
           SunSmart app
        </GelLink>
        .
      </GelParagraph>
      <GelContainerLite
        style={{
          marginTop: 40,
          padding: 40,
          backgroundColor: getGelTokens().global.themeColorBackgroundLight,
        }}
      >
        <GelBoxLayout
          alignment="start"
          alignmentIndividual="start"
          childStyle={{ flex: `0 1 33%` }}
          gutter={32}
        >
          {prevention.map((card, i) => (
            <IconContent key={i} title={card.title} iconName={card.iconName}>
              {card.content}
            </IconContent>
          ))}
        </GelBoxLayout>
        <GelParagraph textAlign="center" marginTop={64}>
          VISIT CANCER COUNCIL VICTORIA'S SUNSMART{" "}
          <GelLink href="https://www.sunsmart.com.au/protect-your-skin" target="_blank">
            WEBSITE{" "}
          </GelLink>
          FOR MORE INFORMATION.
        </GelParagraph>
      </GelContainerLite>
      {/* <GelParagraph
        marginBottom={getGelTokens().global.sizeBaseX4}
        marginTop={getGelTokens().global.sizeBaseX10}
      >
        *BOOKINGS ARE MADE THROUGH HEALTH DIRECT, A GOVERNMENT-FUNDED SERVICE.
        VISIT OUR HEALTH DIRECT FREQUENTLY ASKED QUESTIONS PAGE FOR MORE
        INFORMATION.
      </GelParagraph>
      <GelParagraph>
        STAPLES MP, ELWOOD M, BURTON RC, WILLIAMS JL, MARKS R, GILES GG.
        NON-MELANOMA SKIN CANCER IN AUSTRALIA: THE 2002 NATIONAL SURVEY AND
        TRENDS SINCE 1985 MED J AUST 2006 JAN 2;184(1):6-10. Contact TAL
      </GelParagraph> */}
    </>
  );
};

export default PreventSkinCancer;

const IconContent = ({ iconName, children, title }) => {
  const SContainer = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    marginBottom: 32,
  });

  return (
    <SContainer>
      <GelIcon
        svgCode={renderBrandIcon(iconName)}
        brand
        name={iconName}
        width={80}
        height={80}
        color={getGelTokens().global.themeColorIconDefault}
      ></GelIcon>
      <GelHeading6>{title}</GelHeading6>
      <GelParagraph textAlign="center">{children}</GelParagraph>
    </SContainer>
  );
};
